import React, { useState, useEffect, useMemo } from 'react';
import { getMargins, updateMargins } from '../api/services';

function Configuration({ setFeedback }) {
  const [margins, setMargins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // NUEVO: Estado para la búsqueda

  const fetchMargins = async () => {
    try {
      setIsLoading(true);
      setHasError(false); // Reset error state
      const data = await getMargins();
      setMargins(data || []);
      // Ordenar alfabéticamente al cargar
      const sortedData = (data || []).sort((a, b) => a.Linea.localeCompare(b.Linea));
      setMargins(sortedData);
    } catch (error) {
      console.error('Error fetching margins:', error);
      setHasError(true);
      setFeedback({ message: 'Error al cargar los márgenes. Verifica tus permisos.', type: 'error' });
      // Set empty margins to avoid crashes
      setMargins([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Solo cargar márgenes una vez al montar el componente
    fetchMargins();
  }, []); // Array vacío para que solo se ejecute una vez

  // NUEVO: Memoizar la lista filtrada para optimizar el renderizado
  const filteredMargins = useMemo(() => {
    if (!searchTerm) {
      return margins;
    }
    const lowercasedFilter = searchTerm.toLowerCase();
    return margins.filter(margin =>
      margin.Linea.toLowerCase().includes(lowercasedFilter)
    );
  }, [margins, searchTerm]);

  const handleMarginChange = (linea, newValue) => {
    if (hasError) return; // No permitir cambios si hay error
    
    const updatedMargins = margins.map((margin) =>
      margin.Linea === linea ? { ...margin, Margin: parseFloat(newValue) || 0 } : margin
    );
    setMargins(updatedMargins);
  };

  const handleSaveMargins = async () => {
    if (hasError) {
      setFeedback({ message: 'No se pueden guardar márgenes debido a errores previos.', type: 'error' });
      return;
    }

    try {
      setIsSaving(true);
      setFeedback({ message: 'Guardando márgenes...', type: 'loading' });
      await updateMargins(margins);
      setFeedback({ message: 'Márgenes actualizados correctamente.', type: 'success' });
    } catch (error) {
      console.error('Error saving margins:', error);
      setFeedback({ message: 'Error al guardar los márgenes.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetry = () => {
    fetchMargins();
  };

  if (isLoading) {
    return (
      <section>
        <h2>Configuración de Márgenes</h2>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando configuración...</p>
        </div>
      </section>
    );
  }

  if (hasError) {
    return (
      <section>
        <h2>Configuración de Márgenes</h2>
        <div className="error-state">
          <p>No se pudieron cargar los márgenes. Es posible que no tengas permisos de administrador.</p>
          <button onClick={handleRetry} className="retry-button">
            Reintentar
          </button>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2>Configuración de Márgenes</h2>
      <p className="section-intro">
        Configura los márgenes de beneficio por línea de productos.
      </p>
      
      {/* NUEVO: Cuadro de búsqueda */}
      <div className="search-wrapper">
        <input
          type="text"
          placeholder="Buscar línea..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="margins-table-container">
        <table className="margins-table">
          <thead>
            <tr>
              <th>Línea</th>
              <th>Margen (%)</th>
            </tr>
          </thead>
          <tbody>
            {filteredMargins.map((margin, index) => (
              <tr key={margin.Linea || index}>
                <td>{margin.Linea}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={margin.Margin || 0}
                    onChange={(e) => handleMarginChange(margin.Linea, e.target.value)}
                    disabled={isSaving}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="actions">
        <button 
          onClick={handleSaveMargins} 
          disabled={isSaving || filteredMargins.length === 0}
          className="save-button"
        >
          {isSaving ? 'Guardando...' : 'Guardar Márgenes'}
        </button>
      </div>

      {margins.length === 0 && !hasError && (
        <p className="no-data-message">No hay márgenes para mostrar.</p>
      )}
    </section>
  );
}

export default Configuration;